import {
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	DialogAssignPage,
	DialogClickToType,
	DialogContentType,
	DialogShowPage,
	OnoffType,
	ShowTimeType,
} from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import {
	useDeleteDialogListMutation,
	useGetDialogDetailQuery,
} from '@/api/app-setting/dialogApiSlice';
import InformationInfoForm from './components/DialogInfoForm';
import AppImageView from '@/common/components/form-view/AppImageView';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import PublishedChip from '@/common/components/PublishedChip';
import { DIALOG_LIST_PAGE, PUBLISHED_LISTB } from '@/common/constants/common';

export default function InformationDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteDialogList] = useDeleteDialogListMutation();
	const { dialogId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');

	const dialogContentTypeList = [
		{ id: DialogContentType.Image, name: t('dialogList.image') },
		{ id: DialogContentType.ImageText, name: t('dialogList.imageText') },
		{ id: DialogContentType.Video, name: t('dialogList.video') },
	];

	const showTimeTypeList = [
		{ id: ShowTimeType.Every, name: t('dialogList.every') },
		{ id: ShowTimeType.Once, name: t('dialogList.once') },
		{ id: ShowTimeType.One, name: t('dialogList.one') },
	];

	const dialogClickToTypeList = [
		{ id: DialogClickToType.Null, name: t('dialogList.null') },
		{ id: DialogClickToType.OutSide, name: t('dialogList.outSide') },
		{ id: DialogClickToType.InSide, name: t('dialogList.inSide') },
	];

	const { data: details } = useGetDialogDetailQuery(dialogId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.published == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteDialog'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('dialogList.deleteDialog')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('dialogList.isDeleteDialog')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteDialogList(dialogId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/app-setting/dialog-list');
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.dialogDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('information.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('dialogList.dialogStatus')}
									valueElement={
										<PublishedChip
											published={details?.data.published}
											publishList={PUBLISHED_LISTB}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('information.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('information.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('information.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('information.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('information.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.dialogTitle')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.showPage')}
													value={details?.data.showPageNames || '-'}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.showTime')}
													value={showLabel(
														details?.data.type,
														showTimeTypeList
													)}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.platform')}
													value={
														details?.data?.publishedAndroid &&
														details?.data?.publishedIos
															? 'iOS, Android'
															: details?.data?.publishedAndroid
															? 'Android'
															: details?.data?.publishedIos
															? 'iOS'
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.pushedTime')}
													value={
														!details?.data.publishedStartAt
															? '-'
															: dayjs(details?.data.publishedStartAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.pulledTime')}
													value={
														!details?.data.publishedEndAt
															? '-'
															: dayjs(details?.data.publishedEndAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.contentType')}
													value={showLabel(
														details?.data.mediaType,
														dialogContentTypeList
													)}
												/>
											</Grid>
											{details?.data.mediaType == DialogContentType.Image && (
												<Grid item xs={6}>
													<AppImageView
														label={t('dialogList.dialogImage')}
														imageElement={
															details?.data.image && (
																<img
																	src={details?.data.image?.url}
																	width={390}
																	height={138}
																	style={{
																		objectFit: 'contain',
																	}}
																/>
															)
														}
													/>
												</Grid>
											)}
											{details?.data.mediaType == DialogContentType.ImageText &&
												currentTab == 'zh' && (
													<Grid item xs={12}>
														<RichTextDisplay
															label={t('dialogList.description')}
															editorStateString={
																details?.data.descriptionZh || '-'
															}
														/>
													</Grid>
												)}
											{details?.data.mediaType == DialogContentType.ImageText &&
												currentTab == 'en' && (
													<Grid item xs={12}>
														<RichTextDisplay
															label={t('dialogList.description')}
															editorStateString={
																details?.data.descriptionEn || '-'
															}
														/>
													</Grid>
												)}
											{details?.data.mediaType == DialogContentType.Video && (
												<>
													<Grid item xs={12}>
														<AppFieldView
															label={t('dialogList.dialogVideo')}
															value={details?.data.video?.name || '-'}
															link={details?.data.video?.url}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppImageView
															label={t('dialogList.coverImage')}
															imageElement={
																details?.data.videoCover && (
																	<img
																		src={details?.data.videoCover?.url}
																		width={390}
																		height={138}
																		style={{
																			objectFit: 'contain',
																		}}
																	/>
																)
															}
														/>
													</Grid>
												</>
											)}
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={6}>
												<AppFieldView
													label={t('dialogList.clickTo')}
													value={showLabel(
														details?.data.linkType,
														dialogClickToTypeList
													)}
												/>
											</Grid>
											{details?.data.linkType == DialogClickToType.OutSide && (
												<>
													<Grid item xs={6}>
														<AppFieldView
															label={t('dialogList.dialogLink')}
															value={details?.data.linkUrl || '-'}
															link={details?.data.linkUrl}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('dialogList.dialogLinkButtonText')}
															value={details?.data.linkText}
														/>
													</Grid>
												</>
											)}
											{details?.data.linkType == DialogClickToType.InSide && (
												<>
													<Grid item xs={6}>
														<AppFieldView
															label={t('dialogList.dialogToPage')}
															value={showLabel(
																details?.data.linkPage,
																DIALOG_LIST_PAGE
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('dialogList.dialogLinkButtonText')}
															value={details?.data.linkText}
														/>
													</Grid>
												</>
											)}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('information.text')}
											</Typography>
										</Box>
										<InformationInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
